import { isObjectInList, hasMoreThanHalfNull } from "./util";
import { filterDates, getYearInLong } from "./date";
import { DateTypes } from "./configs";

const group1 = new Set([
  "서울시",
  "경기도",
  "인천시",
  "세종시",
  "대전시",
  "부산시",
  "대구시",
  "울산시",
  "전남광주시",
  "광주시",
  "(구)광주시",
]);

const group2 = new Set([
  "강원도",
  "충청북도",
  "충청남도",
  "경상북도",
  "경상남도",
  "전라북도",
  "전라남도",
  "(구)전라남도",
  "제주도",
]);

const getSidoGroupName = (index) => {
  const nameInfo = index.metadata?.nameInfo || {};
  return nameInfo.displayName || nameInfo.name;
};

export const filterGroup1 = (indexs) => {
  return indexs.filter((i) => group1.has(getSidoGroupName(i)));
};

export const filterGroup2 = (indexs) => {
  return indexs.filter((i) => group2.has(getSidoGroupName(i)));
};

const isGunguInGroupNames = (index, selectedGroupNames) => {
  return index.metadata.nameInfo.gunguGroupNames.some((name) =>
    selectedGroupNames.includes(name)
  );
};

export const filterGungusByGroupNames = (indexs, selectedGroupNames) => {
  return indexs.filter((i) => isGunguInGroupNames(i, selectedGroupNames));
};

const isGunguInGroupName = (index, groupName) => {
  return index.nameInfo.gunguGroupNames.some((name) => name === groupName);
};

export const filterGungusByGroupName = (indexs, groupName) => {
  return indexs.filter((i) => isGunguInGroupName(i, groupName));
};

export const filterDatetimeData = (
  index,
  dates,
  from,
  to = undefined,
  dateType = undefined,
  magnification = 1
) => {
  // year 일경우 한해더 마이너스 해서 올해 초부터 from을 계산함
  const year = getYearInLong(1);
  const { filtered, startIndex } = filterDates(
    dates,
    dateType !== undefined && dateType === DateTypes.YEARLY
      ? from + year
      : from,
    to
  );
  const data = index.data.slice(startIndex);
  const result = [];
  for (let i = 0; i < data.length; i++) {
    result.push([
      filtered[i],
      data[i] != null ? data[i] * magnification : null,
    ]);
  }
  return result;
};

export const isIndexInIds = (index, selectedIds) => {
  // NOTE: 해당 Index가 selectedNames에 포함되는지 확인
  const id = index?.metadata?.nameInfo?.id;
  return id != null && selectedIds.includes(id);
};

export const filterIndexsById = (indexs, selectedIds) => {
  return indexs.filter((i) => isIndexInIds(i, selectedIds));
};

export const isGunguInGunguIds = (index, selectedGungus, filterNull) => {
  // 시도 이름을 넣으려면 group이름까지 확인하면 선택되지 않은 gungu도 무조검 포함될수 있기 때문에
  // 시도는 시도 이름만 비교 필요, 우선은 selectedGungus만 지원하고 시도는 시도 관련 그래프따로
  // 만드는것으로 진행
  return (
    selectedGungus.includes(index?.metadata?.nameInfo?.id) &&
    (!filterNull || !hasMoreThanHalfNull(index.data))
  );
};

export const filterGunguIndexs = (data, selectedGungus, filterNull = false) => {
  return data.filter((i) => isGunguInGunguIds(i, selectedGungus, filterNull));
};

export const calculateMovingAverage = (indexs, dates, windowSize = 5) => {
  const newData = [];
  let newDates = null;

  for (const index of indexs) {
    const moving = _calculateMovingAverage(
      index.data ?? [],
      dates ?? [],
      windowSize
    );
    newData.push({ ...index, data: moving.data, dates: moving.dates });
    if (newDates === null) newDates = moving.dates; // 빈 배열이어도 괜찮음
  }
  return { data: newData, dates: newDates ?? [] };
};

const _calculateMovingAverage = (data, dates, windowSize) => {
  const movingAverageData = [];
  const movingAverageDates = [];

  if (!Array.isArray(data) || !Array.isArray(dates)) {
    return { data: [], dates: [] };
  }

  // 데이터가 충분하지 않으면 빈 배열을 일관되게 반환
  if (data.length < windowSize || dates.length < windowSize) {
    return { data: [], dates: [] };
  }

  for (let i = windowSize - 1; i < data.length; i++) {
    let sum = 0;
    let hasNull = false;

    for (let j = 0; j < windowSize; j++) {
      const value = data[i - j];
      if (value === null) {
        hasNull = true;
        break;
      }
      sum += value;
    }

    movingAverageData.push(hasNull ? null : sum / windowSize);
    movingAverageDates.push(dates[i]);
  }

  return { data: movingAverageData, dates: movingAverageDates };
};

export const generateInDeIndex = (
  indexs,
  dates,
  includeLast = true,
  isRate = false,
  basedAvr = false
) => {
  const newData = [];

  for (const index of indexs) {
    let newIndex = { ...index };

    if (!includeLast) {
      newIndex.data = newIndex.data.slice(0, newIndex.data.length - 1);
    }

    // 평균( null 제외 )
    let avr = null;
    if (basedAvr) {
      const vals = newIndex.data.filter((v) => v !== null && v !== undefined);
      if (vals.length > 0) {
        avr = vals.reduce((sum, v) => sum + v, 0) / vals.length;
      } else {
        avr = null;
      }
    }

    const out = [];
    let before = null;

    for (const d of newIndex.data) {
      let value = 0;

      if (basedAvr) {
        // 평균 대비
        if (d !== null && d !== undefined && avr !== null && avr !== 0) {
          value = d - avr;
          if (isRate) value = (value / avr) * 100;
        } else if (d !== null && d !== undefined && avr === 0) {
          // 평균이 0이면 rate는 0으로(division 방지), 증감은 d-0
          value = d - 0;
          if (isRate) value = 0;
        } else {
          value = 0;
        }
      } else {
        // 이전 값 대비(기존 로직)
        if (before !== null && d !== null && d !== undefined) {
          value = d - before;
          // 절대 증감은 0→현재값도 계산하되, 증감률만 0 나누기를 방지한다.
          if (isRate) value = before !== 0 ? (value / before) * 100 : 0;
        }
        before = d ?? before; // d가 null이면 이전값 유지
      }

      out.push(value);
    }

    newData.push({ ...newIndex, data: out });
  }

  let newDates = [...dates];
  if (!includeLast) {
    newDates = newDates.slice(0, newDates.length - 1);
  }

  return {
    data: newData,
    dates: newDates,
  };
};

export const getBiggestClosestData = (
  indexs,
  dates,
  xPoint,
  adjustNull = true
) => {
  // indexs 들중에 가장 데이터가 작은 쪽에 closestIndex를 맞추어 줘야함
  let closestIndex = 0;
  let closestXPoint = xPoint;
  let smallestDiff = Math.abs(xPoint - dates[0]);

  // 모든 dates를 순회하면서 가장 작은 차이 찾기
  for (let i = 1; i < dates.length; i++) {
    let diff = Math.abs(xPoint - dates[i]);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      closestIndex = i;
    }
  }

  if (adjustNull) {
    // 이건 기준선으로 0부터 시작할때
    // 기준선 보다 뒤에서 시작하는 데이터들은 시작 시점을 null이
    // 아닌 데이터 부터 해줘야함.
    for (var index of indexs) {
      var value = index.data[closestIndex];
      var current = closestIndex;
      if (value == null) {
        while (value === null) {
          value = index.data[current];
          current += 1;
          if (current >= index.data.length) {
            break;
          }
        }
      }
      if (current > closestIndex) {
        closestIndex = current;
        closestXPoint = dates[closestIndex];
      }
    }
  } else {
    // 근데 시계열 표시 할때 (annotation에서)
    // adjustNull을 해버리면 closestIndex가 달라지고 전체 데이터 갯수도
    // 안맞기 때문에 시계열 표시가 이상해짐.
    // (merge를 통해 전체 데이터를 마추어 두었기 때문에 모든 데이터는 동일한 range를 가져야 함.)
    closestXPoint = dates[closestIndex];
  }

  return { closestIndex, closestXPoint };
};

// ✅ helper: DataIndex[] (혹은 raw list여도 item.data가 있으면 동작) + dates 받아서
// 앞 null 정렬 + 첫 non-null 이후 null 나오면 그 뒤는 버림 + 길이 동일화 처리
export const filterFrontNullDataIndexes = (dataIndexes, dates) => {
  if (!Array.isArray(dataIndexes) || dataIndexes.length === 0) {
    return { data: [], dates: Array.isArray(dates) ? dates : [] };
  }

  const isNull = (v) => v === null || v === undefined;

  // 각 series의 leading null(앞 연속 null)과, lead 이후 첫 null 위치(end) 계산
  const metas = dataIndexes.map((idx) => {
    const arr = idx?.data ?? [];

    let lead = 0;
    while (lead < arr.length && isNull(arr[lead])) lead++;

    let end = arr.length;
    for (let i = lead; i < arr.length; i++) {
      if (isNull(arr[i])) {
        end = i; // i부터는 버림
        break;
      }
    }

    return { lead, end };
  });

  // 전부 값이 없으면 dates/data를 비움
  const hasAnyValue = metas.some((m) => m.end - m.lead > 0);
  if (!hasAnyValue)
    return {
      data: dataIndexes.map((d) => ({ ...d, data: [], dates: [] })),
      dates: [],
    };

  // leading null 가장 긴 series 기준 start
  let start = metas[0].lead;
  for (const m of metas) start = Math.max(start, m.lead);

  // 최종 길이 = 모든 series가 start~end까지 가질 수 있는 최소 길이
  let finalLen = Infinity;
  for (const m of metas) {
    const len = Math.max(0, m.end - start);
    finalLen = Math.min(finalLen, len);
  }
  if (!Number.isFinite(finalLen) || finalLen < 0) finalLen = 0;

  const newDates = (dates ?? []).slice(start, start + finalLen);

  // dataIndexes는 DataIndex 인스턴스일 수도 있고 raw object일 수도 있음.
  // DataResponse 생성 흐름에 맞추려면 raw object 배열로 리턴하는 게 안전.
  const newData = dataIndexes.map((idx) => {
    const srcData = idx?.data ?? [];
    // idx는 raw object라고 가정하고 얕은 복사 + data만 교체
    return {
      ...idx,
      data: srcData.slice(start, start + finalLen),
      // dates 필드는 raw에 있을 수도/없을 수도 있으니 있으면 맞춰주고 아니면 생략
      ...(Array.isArray(idx?.dates)
        ? { dates: idx.dates.slice(start, start + finalLen) }
        : {}),
    };
  });

  return { data: newData, dates: newDates };
};

export const filterDataFromDate = (indexs, dates, date, inclusive = false) => {
  // 1. 기준 date보다 크거나 같은(inclusive) 또는 큰 인덱스 찾기
  const filteredIndexes = dates
    .map((d, i) => (inclusive ? d >= date : d > date) ? i : -1)
    .filter((i) => i !== -1);

  // 2. 필터링된 dates 생성
  const filteredDates = filteredIndexes.map((i) => dates[i]);

  // 3. indexs.data도 같은 위치만 남기기
  const filteredIndexs = indexs.map((index) => {
    return {
      ...index,
      data: filteredIndexes.map((i) => index.data[i]),
    };
  });

  return {
    data: filteredIndexs,
    dates: filteredDates,
  };
};
export const filterDataFromXPoint = (indexs, dates, xPoint) => {
  // xPoint와 가장 가까운 값을 찾기 위해 가장 작은 차이를 계산
  const result = [];
  let { closestIndex, closestXPoint } = getBiggestClosestData(
    indexs,
    dates,
    xPoint
  );

  for (var index of indexs) {
    // 변경 전의 데이터 값을 저장
    const data = [...index.data];

    // 이 시리즈에서만 사용하는 유효 인덱스 탐색 (closestIndex부터 앞으로)
    let validIndex = closestIndex;
    while (validIndex < data.length && data[validIndex] == null) {
      validIndex++;
    }

    const originalValue = data[validIndex];
    // 가장 근접한 index의 data 값을 0으로 설정
    data[validIndex] = 0;
    // 변경된 값의 차이만큼 다른 모든 data 값들을 조정
    const changeAmount = -originalValue; // 0으로 만들었으므로 원래 값에 음수를 취함

    for (let i = 0; i < data.length; i++) {
      if (i !== validIndex) {
        if (data[i] !== null) {
          data[i] += changeAmount;
        }
      }
    }
    result.push({ ...index, data });
  }

  return { data: result, closestIndex, closestXPoint };
};

export const filterGeoJsonById = (data, id, bboxRatio = 1) => {
  const filteredFeatures = data.features.filter(
    (feature) => feature?.properties?.metadata?.nameInfo?.id === id
  );
  return generateNewGeoJson(data, filteredFeatures, bboxRatio);
};

export const filterGeoJsonByIds = (data, ids, bboxRatio = 1) => {
  const filteredFeatures = data.features.filter((feature) =>
    ids.includes(feature?.properties?.metadata?.nameInfo?.id)
  );
  return generateNewGeoJson(data, filteredFeatures, bboxRatio);
};

export const filterGungusGeoJson = (data, sidoName, bboxRatio = 1) => {
  // Step 1: Filter the features by sidoName
  const filteredFeatures = data.features.filter(
    (feature) => feature?.properties?.nameInfo?.sidoName === sidoName
  );

  return generateNewGeoJson(data, filteredFeatures, bboxRatio);
};

/**
 * GeoJSON Polygon/MultiPolygon의 모든 좌표쌍을 순회한다.
 * 전북/전남처럼 첫 ring이 작은 섬인 경우 coordinates[0]만 보면 bbox가 섬으로 잡힌다.
 */
const walkGeoCoordinates = (coordinates, onPoint) => {
  if (!Array.isArray(coordinates)) return;
  if (
    coordinates.length >= 2 &&
    typeof coordinates[0] === "number" &&
    typeof coordinates[1] === "number"
  ) {
    onPoint(coordinates[0], coordinates[1]);
    return;
  }
  coordinates.forEach((child) => walkGeoCoordinates(child, onPoint));
};

export const generateNewGeoJson = (data, filteredFeatures, bboxRatio = 1) => {
  if (filteredFeatures.length === 0) {
    throw new Error(`No features found`);
  }

  // Step 2: Calculate the bounding box
  let minx = Infinity,
    miny = Infinity,
    maxx = -Infinity,
    maxy = -Infinity;

  filteredFeatures.forEach((feature) => {
    walkGeoCoordinates(feature.geometry.coordinates, (x, y) => {
      if (x < minx) minx = x;
      if (y < miny) miny = y;
      if (x > maxx) maxx = x;
      if (y > maxy) maxy = y;
    });
  });

  // Calculate the center, width, and height of the bbox
  const centerX = (minx + maxx) / 2;
  const centerY = (miny + maxy) / 2;
  const width = maxx - minx;
  const height = maxy - miny;

  // Step 3: Adjust bbox size using bboxRatio
  const adjustedWidth = width * bboxRatio;
  const adjustedHeight = height * bboxRatio;

  const adjustedMinX = centerX - adjustedWidth / 2;
  const adjustedMaxX = centerX + adjustedWidth / 2;
  const adjustedMinY = centerY - adjustedHeight / 2;
  const adjustedMaxY = centerY + adjustedHeight / 2;

  // Step 4: Update the bbox in the data
  data.bbox = [adjustedMinX, adjustedMinY, adjustedMaxX, adjustedMaxY];

  // Step 5: Return the filtered data with the updated bbox
  return {
    ...data,
    features: filteredFeatures,
  };
};

export const generateAptBBox = (apts, bboxRatio = 1, minBBoxSize = 0.01) => {
  if (apts.length === 0) {
    return undefined;
  }

  // Initialize bounding box values
  let minLat = Infinity,
    minLng = Infinity,
    maxLat = -Infinity,
    maxLng = -Infinity;

  // Loop through each apartment to find the min/max latitude and longitude
  apts.forEach((apt) => {
    const { latitude, longitude } = apt;
    if (latitude < minLat) minLat = latitude;
    if (longitude < minLng) minLng = longitude;
    if (latitude > maxLat) maxLat = latitude;
    if (longitude > maxLng) maxLng = longitude;
  });

  // Calculate the center, width, and height of the bbox
  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;
  let width = maxLng - minLng;
  let height = maxLat - minLat;

  // Ensure minimum BBox size for single point
  if (width === 0) width = minBBoxSize;
  if (height === 0) height = minBBoxSize;

  // Adjust bbox size using bboxRatio
  const adjustedWidth = width * bboxRatio;
  const adjustedHeight = height * bboxRatio;

  const adjustedMinLng = centerLng - adjustedWidth / 2;
  const adjustedMaxLng = centerLng + adjustedWidth / 2;
  const adjustedMinLat = centerLat - adjustedHeight / 2;
  const adjustedMaxLat = centerLat + adjustedHeight / 2;

  // Return the adjusted bbox as an array
  return [adjustedMinLng, adjustedMinLat, adjustedMaxLng, adjustedMaxLat];
};

export const generateBBox = (latitude, longitude, wide = 0.015) => {
  const adjustedMinLng = longitude - wide;
  const adjustedMaxLng = longitude + wide;
  const adjustedMinLat = latitude - wide;
  const adjustedMaxLat = latitude + wide;

  return [adjustedMinLng, adjustedMinLat, adjustedMaxLng, adjustedMaxLat];
};

export const generateBBoxByCenterZoom = (
  latitude,
  longitude,
  zoom,
  mapWidthPx = 1400,
  mapHeightPx = 700,
  boxRatio = 1
) => {
  // 1) zoom 레벨에서 pixel당 경도 변화량
  const lngPerPixel = 360 / (256 * Math.pow(2, zoom));

  // 2) 위도 보정 (Mercator)
  const latPerPixel = lngPerPixel * Math.cos((latitude * Math.PI) / 180);

  const halfWidthLng = (mapWidthPx / 2) * lngPerPixel * boxRatio;
  const halfHeightLat = (mapHeightPx / 2) * latPerPixel * boxRatio;

  const minLng = longitude - halfWidthLng;
  const maxLng = longitude + halfWidthLng;
  const minLat = latitude - halfHeightLat;
  const maxLat = latitude + halfHeightLat;

  return [minLng, minLat, maxLng, maxLat];
};
